/**
 * Where a thing's frame is drawn (ADR-040 amendment of 2026-10-04, P1.6-T83). A thing is lowered to an entity
 * at its feet point, which a game's server may hold on a coarser grain than the author's pixel; its appearance
 * carries the pixels that rounding took away (`offset`, P1.1-T7). The frame a state shows is drawn centred on
 * the feet point, standing on it, moved by that offset, which puts it back on the author's pixel. Everything
 * laid on the collision cells for a thing (its footprint, its bounds, its ground line, its activation cells)
 * starts from this one place, so the cells follow the art and never the rounded point.
 */

/**
 * The x of a drawn frame's anchor, its left edge in tiles. Allocates nothing.
 *
 * @param feetX The feet point's x in tiles.
 * @param frameWidth The pixel width of the frame drawn.
 * @param offsetX The appearance's pixel offset on x, 0 when it carries none.
 * @param tileSize Tile size in pixels.
 * @returns The anchor's x in tiles.
 */
export function drawnFrameX(feetX: number, frameWidth: number, offsetX: number, tileSize: number): number {
  return feetX - frameWidth / (2 * tileSize) + offsetX / tileSize;
}

/**
 * The y of a drawn frame's anchor in tiles: the top of the tile row the frame stands on, one tile above its
 * bottom edge. Allocates nothing.
 *
 * @param feetY The feet point's y in tiles.
 * @param offsetY The appearance's pixel offset on y, 0 when it carries none.
 * @param tileSize Tile size in pixels.
 * @returns The anchor's y in tiles.
 */
export function drawnFrameY(feetY: number, offsetY: number, tileSize: number): number {
  return feetY - 1 + offsetY / tileSize;
}

/**
 * The anchor a thing's frame is drawn at, in tiles: the bottom-left of the frame sits on the bottom-left of
 * the anchor cell, as a prop's does, so `anchor.x * tileSize` is the frame's left edge and
 * `(anchor.y + 1) * tileSize` its bottom edge in pixels.
 *
 * @param feet The feet point in tiles, as the game holds it.
 * @param frameWidth The pixel width of the frame drawn.
 * @param offset The appearance's pixel offset, undefined when it carries none.
 * @param tileSize Tile size in pixels.
 * @returns The anchor in tiles.
 * @example drawnFrameAnchor({ x: 128.375, y: 127.125 }, 128, { x: -1, y: -1 }, 32) is { x: 126.34375, y: 126.09375 }.
 */
export function drawnFrameAnchor(feet: { readonly x: number; readonly y: number }, frameWidth: number, offset: { readonly x: number; readonly y: number } | undefined, tileSize: number): { x: number; y: number } {
  return { x: drawnFrameX(feet.x, frameWidth, offset?.x ?? 0, tileSize), y: drawnFrameY(feet.y, offset?.y ?? 0, tileSize) };
}
